import { useState } from 'react'
import {
  slugifyTenant,
  type TenantSummary,
  tenantIntegrations,
  useAdminTenant,
  useAdminTenants,
  useCreateTenant,
  useDeleteTenant,
  useRevealTenantCredential,
  useUpdateTenantCredentials,
} from '../../../features/admin/use-admin-tenants'
import { VENDOR_KEYS, vendorBlock } from '../../../features/admin/vendors'
import { useOrgId } from '../../use-org-id'
import { AdminPage, adminError, td, VendorEntry } from './admin-bits'
import { Btn, ConfirmDialog, Dialog, Field, Inline, input, Select, Tag, th } from './controls'
import { LedgerLink, mono, Note, Section, serif } from './ui'

function CreateDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateTenant()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [touched, setTouched] = useState(false)
  const [banner, setBanner] = useState('')
  const effSlug = touched ? slug : slugifyTenant(name)
  const valid = name.trim() !== '' && effSlug !== ''
  const submit = () =>
    create.mutate({ name: name.trim(), slug: effSlug, banner: banner || undefined }, { onSuccess: onClose })
  return (
    <Dialog
      open
      eyebrow="New entry"
      title="Create a tenant"
      onClose={onClose}
      onSubmit={() => valid && !create.isPending && submit()}
      footer={
        <>
          <Btn onClick={onClose} disabled={create.isPending}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={!valid || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create tenant'}
          </Btn>
        </>
      }
    >
      <Field label="Name">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Slug" hint="Lowercase letters, numbers and dashes. Must be unique.">
        <input
          className={input}
          style={mono}
          value={effSlug}
          onChange={(e) => {
            setTouched(true)
            setSlug(e.target.value)
          }}
        />
      </Field>
      <Field label="Banner">
        <Select
          value={banner}
          onChange={setBanner}
          placeholder="None"
          options={[
            ['dtlr', 'DTLR'],
            ['spc', 'SPC'],
          ]}
        />
      </Field>
      {create.error ? <Inline tone="bad">{adminError(create.error)}</Inline> : null}
    </Dialog>
  )
}

export function LedgerAdminTenants() {
  const tenants = useAdminTenants()
  const del = useDeleteTenant()
  const [creating, setCreating] = useState(false)
  const [remove, setRemove] = useState<TenantSummary | null>(null)
  const list = tenants.data ?? []
  return (
    <AdminPage
      title="Tenants"
      standfirst="Organizations and the integrations enabled for each."
      loading={tenants.isLoading}
      error={!!tenants.error}
      aside={
        <Btn variant="primary" onClick={() => setCreating(true)}>
          Create tenant
        </Btn>
      }
    >
      <Section title={`${list.length} ${list.length === 1 ? 'tenant' : 'tenants'}`}>
        {list.length === 0 ? (
          <Note>No tenants yet.</Note>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>Name</th>
                <th className={th}>Slug</th>
                <th className={th}>Banner</th>
                <th className={th}>Integrations</th>
                <th className={`${th} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id}>
                  <td className={`${td} text-[16px] font-semibold`} style={serif}>
                    <LedgerLink to={`/admin/tenants/${encodeURIComponent(t.id)}`}>{t.name}</LedgerLink>
                  </td>
                  <td className={`${td} text-[12.5px]`} style={mono}>
                    {t.slug}
                  </td>
                  <td className={`${td} text-[var(--color-muted)]`}>{t.banner?.toUpperCase() ?? '-'}</td>
                  <td className={td}>
                    <div className="flex flex-wrap gap-1.5">
                      {tenantIntegrations(t).map((i) => (
                        <Tag key={i.vendor} tone={i.enabled ? 'ok' : 'off'}>
                          {i.label}
                        </Tag>
                      ))}
                    </div>
                  </td>
                  <td className={`${td} text-right`}>
                    <Btn variant="danger" onClick={() => setRemove(t)}>
                      Delete
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {creating ? <CreateDialog onClose={() => setCreating(false)} /> : null}
      <ConfirmDialog
        open={remove !== null}
        title="Delete this tenant?"
        body={<>{remove?.name} and its stored credentials will be permanently removed. This cannot be undone.</>}
        confirmLabel="Delete tenant"
        danger
        pending={del.isPending}
        error={del.error ? adminError(del.error) : null}
        onConfirm={() => remove && del.mutate({ orgId: remove.id }, { onSuccess: () => setRemove(null) })}
        onClose={() => {
          del.reset()
          setRemove(null)
        }}
      />
    </AdminPage>
  )
}

export function LedgerAdminTenantDetail() {
  const orgId = useOrgId()
  const tenant = useAdminTenant(orgId)
  const update = useUpdateTenantCredentials()
  const reveal = useRevealTenantCredential()
  return (
    <AdminPage
      title="Tenant credentials"
      standfirst={
        <>
          Integration settings for <span style={mono}>{orgId}</span>.{' '}
          <LedgerLink to="/admin/tenants">All tenants</LedgerLink>
        </>
      }
      loading={tenant.isLoading}
      error={!!tenant.error}
    >
      {VENDOR_KEYS.map((v) => (
        <VendorEntry
          key={v}
          vendor={v}
          block={vendorBlock(tenant.data, v)}
          pending={update.isPending && update.variables?.vendor === v}
          error={update.variables?.vendor === v ? update.error : null}
          onSave={(body) => update.mutate({ orgId, ...body })}
          reveal={(vendor, field) => reveal.mutateAsync({ orgId, vendor, field })}
        />
      ))}
    </AdminPage>
  )
}
